import { useCallback, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { getMaquinaByComuna } from "@/api";
import { normalizarListaMaquinas } from "@/lib/mapaUbicaciones";

// Estado *por instancia* del modal "Ubicación de retiro" (Carrusel /
// OfertaDelDia). Reemplaza el acoplamiento anterior por cache global
// (queryClient.getQueryData(['ListaMaquinasByComuna']) +
// useIsMutating({mutationKey:['mutateListaMaquinasByComuna']})), que hacía
// que los dos modales montados simultáneamente en el Home se interfirieran
// entre sí (operar uno activaba el skeleton del otro, y removeQueries de uno
// borraba lo que el otro mostraba — ver CLAUDE.md § modal de ubicaciones).
//
// `reqIdRef` da "last-wins": sólo la respuesta cuya solicitud es la más
// reciente actualiza el estado, así que un cambio rápido de comuna (o cerrar
// el modal con una petición en vuelo) nunca deja el mapa/skeleton en un
// estado obsoleto. Mismo espíritu que el ownership por token de
// paymentGuard.js, pero mucho más simple (no hay candado que liberar, sólo
// un id que comparar).
export const useMaquinasPorComuna = () => {
  const [estado, setEstado] = useState("idle"); // idle | cargando | ok | error
  const [maquinas, setMaquinas] = useState([]);
  const reqIdRef = useRef(0);

  const mutation = useMutation({ mutationFn: getMaquinaByComuna });

  const cargarComuna = useCallback(
    ({ idRegion, idComuna }) => {
      const reqId = ++reqIdRef.current;
      setEstado("cargando");

      mutation
        .mutateAsync({ idRegion, idComuna })
        .then((payload) => {
          if (reqIdRef.current !== reqId) return; // respuesta obsoleta: descartada en silencio

          const { maquinas: lista, estado: estadoNormalizado } = normalizarListaMaquinas(payload);

          if (estadoNormalizado === "invalido") {
            Sentry.captureMessage("Respuesta no-array de listaMaquinas (modal ubicaciones)", {
              level: "warning",
              tags: { feature: "ubicaciones_modal" },
              extra: { status: payload?.status, message: payload?.message },
            });
            setMaquinas([]);
            setEstado("error");
            return;
          }

          setMaquinas(lista);
          setEstado("ok");
        })
        .catch((error) => {
          if (reqIdRef.current !== reqId) return; // respuesta obsoleta: descartada en silencio

          Sentry.captureException(error, {
            tags: { feature: "ubicaciones_modal" },
          });
          setMaquinas([]);
          setEstado("error");
        });
    },
    [mutation],
  );

  // Invalida cualquier respuesta en vuelo (no hay reqId que la haga vigente
  // de nuevo) y resetea el estado local, sin tocar ningún cache compartido.
  const reset = useCallback(() => {
    reqIdRef.current += 1;
    setEstado("idle");
    setMaquinas([]);
  }, []);

  return { maquinas, estado, cargarComuna, reset };
};
